import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, CheckCircle2, Trophy, Zap, ShieldCheck, MonitorPlay } from "lucide-react";
import { Layout } from "../components/Layout";
import { Seo, faqSchema } from "../components/Seo";
import { MaskedLines, Reveal, FadeIn } from "../components/motion";
import { AffiliateButton, ScoreBadge } from "../components/Affiliate";
import { VpnCard } from "../components/VpnCard";
import { ComparisonTable } from "../components/ComparisonTable";
import { FaqAccordion } from "../components/FaqAccordion";
import { CtaBanner } from "../components/CtaBanner";
import { VPNS, NORD } from "../data/vpns";
import { FAQS } from "../data/faqs";
import { BEST_FOR } from "../data/bestFor";
import { VpnLogo } from "../components/VpnLogo";
import { useState, useEffect } from "react";
import { api } from "../lib/api";

const TRUST = [
  "5 VPNs tested across 47 server locations",
  "Updated monthly with latest pricing and features",
  "Editorially independent — we rank on merit",
];

const HERO_STATS = [
  { label: "Speed retained", value: "95%", width: "95%" },
  { label: "Streaming unblocked", value: "100%", width: "100%" },
  { label: "Servers", value: "6,400+ · 111 countries", width: null },
  { label: "No-log audits", value: "PwC + Deloitte", width: null },
];

const HeroNordCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 48 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className="relative hidden lg:block"
    data-testid="hero-nord-card"
  >
    <div className="pointer-events-none absolute -inset-8 rounded-full bg-[#4687FF]/15 blur-[80px]" />
    <motion.div
      animate={{ y: [0, -10, 0] }}
      transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
      className="relative border border-white/15 bg-white/[0.06] p-8 backdrop-blur-xl"
    >
      <div className="absolute -top-3.5 left-8 flex items-center gap-1.5 bg-[#4687FF] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
        <Trophy size={12} /> Editor's Choice 2026
      </div>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <VpnLogo vpn={NORD} size={64} tile />
          <div>
            <p className="font-display text-2xl font-extrabold tracking-tight text-white">NordVPN</p>
            <p className="text-xs font-semibold text-slate-400">#1 of 5 VPNs tested</p>
          </div>
        </div>
        <ScoreBadge score={NORD.score} />
      </div>
      <div className="mt-7 space-y-4">
        {HERO_STATS.map((s) => (
          <div key={s.label}>
            <div className="flex items-baseline justify-between text-[13px]">
              <span className="font-semibold text-slate-300">{s.label}</span>
              <span className="font-display font-bold text-white">{s.value}</span>
            </div>
            {s.width && (
              <div className="mt-1.5 h-1 w-full bg-white/10">
                <motion.div
                  className="h-full bg-[#4687FF]"
                  initial={{ width: 0 }}
                  animate={{ width: s.width }}
                  transition={{ delay: 1.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-8">
        <AffiliateButton href={NORD.link} testId="hero-card-cta" className="w-full">
          Get NordVPN from $3.09/mo
        </AffiliateButton>
        <p className="mt-3 text-center text-[11px] font-medium text-slate-400">
          30-day money-back guarantee · No logs, audited
        </p>
      </div>
    </motion.div>
  </motion.div>
);


const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.25]);
  return (
    <section className="grain relative overflow-hidden bg-[#0F172A]" data-testid="hero-section">
      <div className="hero-grid absolute inset-0" />
      <motion.div
        style={{ y, opacity }}
        className="pointer-events-none absolute -right-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#4687FF]/20 blur-[140px]"
      />
      <div className="relative mx-auto max-w-7xl px-6 pb-28 pt-20 lg:px-8 lg:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-[1fr_400px] xl:gap-20">
          <div>
            <FadeIn delay={0.05}>
              <p className="mb-8 inline-flex items-center gap-2 border border-[#4687FF]/40 bg-[#4687FF]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4687FF]" /> 2026 Rankings — Live
              </p>
            </FadeIn>
            <h1 className="max-w-5xl font-display text-4xl font-black tracking-tighter text-white sm:text-6xl lg:text-7xl">
              <MaskedLines lines={["The Best VPNs of 2026:", "Independently Tested", "& Ranked."]} />
            </h1>
            <FadeIn delay={0.7}>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                We've spent hundreds of hours testing speed, security, streaming, and privacy across 5 leading VPN
                services. Our rankings are based on real data — not affiliate payouts. Here's who wins.
              </p>
            </FadeIn>
            <FadeIn delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
              <AffiliateButton href={NORD.link} testId="hero-nordvpn-cta" className="!px-9 !py-4 !text-base">
                Try NordVPN — Our #1 Pick
              </AffiliateButton>
              <Link
                to="/best-vpns"
                data-testid="hero-rankings-link"
                className="group inline-flex items-center gap-2 px-2 py-4 font-display text-sm font-bold text-white transition-colors duration-200 hover:text-[#4687FF]"
              >
                See Full Rankings
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </FadeIn>
          </div>
          <HeroNordCard />
        </div>
        <FadeIn delay={1.1}>
          <div className="mt-16 grid gap-4 border-t border-white/10 pt-8 sm:grid-cols-3" data-testid="hero-trust-bar">
            {TRUST.map((t, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#4687FF]" />
                {t}
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

const WinnerCard = () => (
  <section className="relative z-10 mx-auto -mt-14 max-w-5xl px-6 lg:px-8" data-testid="winner-card">
    <Reveal>
      <div className="border-2 border-[#4687FF] bg-white p-8 shadow-2xl shadow-[#4687FF]/10 sm:p-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          <div className="flex flex-wrap items-center gap-5 sm:gap-6">
            <VpnLogo vpn={NORD} size={88} />
            <ScoreBadge score={NORD.score} size="lg" />
            <div className="lg:hidden">
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#4687FF]">
                <Trophy size={14} /> Our #1 VPN for 2026
              </p>
              <h2 className="mt-1 font-display text-3xl font-black tracking-tighter text-[#0F172A]">NordVPN</h2>
            </div>
          </div>
          <div className="flex-1">
            <p className="hidden items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#4687FF] lg:flex">
              <Trophy size={14} /> Our #1 VPN for 2026
            </p>
            <h2 className="hidden font-display text-3xl font-black tracking-tighter text-[#0F172A] lg:block">
              NordVPN — Score: 9.8/10
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              After testing 5 VPNs across speed, security, streaming, and ease of use, NordVPN is our clear
              recommendation. It's the fastest in our tests, unblocks Netflix and BBC iPlayer reliably, and its
              independently-audited no-log policy means your data genuinely stays private. Plans start from
              $3.09/month on a 2-year deal.
            </p>
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {NORD.pros.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] leading-snug text-slate-700">
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#4687FF]" /> {p}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <AffiliateButton href={NORD.link} testId="winner-card-cta">
                Get NordVPN from $3.09/mo
              </AffiliateButton>
              <span className="text-xs font-medium text-slate-500">{NORD.details}</span>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

const CATEGORY_ICONS = { streaming: MonitorPlay, gaming: Zap, privacy: ShieldCheck };

const Home = () => {
  const [latestPosts, setLatestPosts] = useState([]);

  useEffect(() => {
    api.get("/posts?limit=3").then((r) => setLatestPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
  <Layout>
    <Seo
      title="Best VPN 2026: Top 5 VPNs Tested, Ranked & Compared | VPNReviews.ai"
      description="We independently tested 5 leading VPNs in 2026 — NordVPN, Surfshark, PureVPN, VeePN and AdGuard. See our full rankings, comparison table and expert recommendations."
      schema={faqSchema(FAQS)}
    />
    <Hero />

    {/* Editorial marquee */}
    <div className="border-y border-slate-200 bg-white py-4" data-testid="category-marquee">
      <Marquee speed={35} gradient={false} pauseOnHover>
        {BEST_FOR.map((b) => (
          <Link
            key={b.slug}
            to={`/best-vpn-for/${b.slug}`}
            className="mx-8 font-display text-sm font-bold uppercase tracking-[0.25em] text-slate-400 transition-colors duration-200 hover:text-[#4687FF]"
          >
            Best VPN for {b.label} <span className="ml-8 text-[#4687FF]">·</span>
          </Link>
        ))}
      </Marquee>
    </div>

    {/* Top 5 ranked cards */}
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-8" data-testid="top5-section">
      <Reveal className="mb-14 max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">01 — The Rankings</p>
        <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
          The Top 5 VPNs, Tested & Scored
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Every VPN below went through the same test suite: 47 server locations, 7 streaming services, speed
          benchmarks, and privacy verification.
        </p>
      </Reveal>
      <div className="space-y-6">
        {VPNS.map((v, i) => (
          <VpnCard key={v.slug} vpn={v} index={i} />
        ))}
      </div>
    </section>

    {/* Comparison table */}
    <section className="border-y border-slate-200 bg-slate-50 py-28" data-testid="home-comparison-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mb-12 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">02 — Head to Head</p>
          <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
            Full Feature Comparison
          </h2>
        </Reveal>
        <Reveal>
          <div className="border border-slate-200 bg-white p-6 sm:p-8">
            <ComparisonTable />
          </div>
        </Reveal>
        <Reveal className="mt-8">
          <Link to="/best-vpns" data-testid="home-bestvpns-link" className="inline-flex items-center gap-2 font-display text-sm font-bold text-[#4687FF] hover:text-[#2563eb]">
            See the full Best VPNs breakdown <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>

    <WinnerCard />

    {/* Category teasers */}
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-8" data-testid="category-teasers">
      <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">03 — By Use Case</p>
          <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
            Best VPN For...
          </h2>
        </div>
        <Link to="/best-vpn-for" className="font-display text-sm font-bold text-[#4687FF] hover:text-[#2563eb]">
          All 12 categories →
        </Link>
      </Reveal>
      <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
        {BEST_FOR.slice(0, 6).map((b, i) => {
          const Icon = CATEGORY_ICONS[b.slug] || ShieldCheck;
          return (
            <Reveal key={b.slug} delay={i * 0.05}>
              <Link
                to={`/best-vpn-for/${b.slug}`}
                data-testid={`category-teaser-${b.slug}`}
                className="group block h-full bg-white p-8 transition-colors duration-200 hover:bg-[#0F172A]"
              >
                <Icon size={22} className="text-[#4687FF]" />
                <h3 className="mt-5 font-display text-lg font-extrabold tracking-tight text-[#0F172A] transition-colors duration-200 group-hover:text-white">
                  Best VPN for {b.label}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500 transition-colors duration-200 group-hover:text-slate-300">
                  {b.intro[0].slice(0, 110)}…
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#4687FF]">
                  Read ranking <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>

    {/* Latest posts */}
    <section className="border-t border-slate-200 bg-slate-50 py-28" data-testid="latest-posts-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">04 — From the Blog</p>
            <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
              Latest Analysis
            </h2>
          </div>
          <Link to="/blog" className="font-display text-sm font-bold text-[#4687FF] hover:text-[#2563eb]">
            All articles →
          </Link>
        </Reveal>
        <div className="grid gap-6 lg:grid-cols-3">
          {latestPosts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.07}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`home-blog-card-${p.slug}`}
                className="group flex h-full flex-col border border-slate-200 bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4687FF]">{p.category}</span>
                <h3 className="mt-3 font-display text-lg font-extrabold leading-snug tracking-tight text-[#0F172A] group-hover:text-[#4687FF]">
                  {p.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{p.excerpt}</p>
                <span className="mt-5 text-xs font-medium text-slate-400">
                  {p.date} · {p.reading_time}
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="mx-auto max-w-4xl px-6 py-28 lg:px-8" data-testid="home-faq-section">
      <Reveal className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4687FF]">05 — Questions</p>
        <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#0F172A] sm:text-4xl">
          VPN FAQs, Answered Honestly
        </h2>
      </Reveal>
      <Reveal>
        <FaqAccordion faqs={FAQS} />
      </Reveal>
    </section>

    <CtaBanner />
  </Layout>
  );
};

export default Home;
